import React, { useState } from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';
import { useSettingsReadModel } from './use-settings-read-model.js';
import type { Panel, EmailChannelBlock } from './use-settings-read-model.js';
import PermissionsPanel from './PermissionsPanel.js';
import { useDiagMode } from '../../lib/use-diag-mode.js';

function PanelShell<T>({ title, panel, children }: { title: string; panel: Panel<T>; children: (data: T) => React.ReactNode }) {
  return (
    <div style={LAYOUT.subPanel}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12, gap: 8, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold }}>
          {title}
        </div>
        {panel.loading && <div style={{ color: C.textMuted, fontSize: 11 }}>Loading…</div>}
      </div>
      {panel.error && (
        <div style={{ color: C.textMuted, fontSize: 12 }}>
          Unavailable: {panel.error}
        </div>
      )}
      {!panel.loading && !panel.error && panel.data && children(panel.data)}
    </div>
  );
}

function KV({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div style={{ padding: 10, borderRadius: 8, background: C.panel, border: `1px solid ${C.borderSoft}` }}>
      <div style={{ color: C.textMuted, fontSize: 11, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ color: accent || C.text, fontSize: 13, wordBreak: 'break-word' }}>{value}</div>
    </div>
  );
}

function ChannelCard({ title, block }: { title: string; block?: EmailChannelBlock }) {
  if (!block) return null;
  const recipients = block.recipients || block.to || [];
  return (
    <div style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}`, display: 'grid', gap: 8 }}>
      <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{title}</div>
      <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
        <KV label="From" value={block.from || '—'} />
        <KV
          label="Recipients"
          value={recipients.length ? recipients.join(', ') : '—'}
        />
        {block.cron && <KV label="Cron" value={block.cron} />}
        {block.cron_description && <KV label="Schedule" value={block.cron_description} />}
        {block.manual_trigger && <KV label="Manual trigger" value={block.manual_trigger} />}
        {block.trigger && <KV label="Trigger" value={block.trigger} />}
      </div>
    </div>
  );
}

function formatFlagUpdatedAt(ts: string | null | undefined): string {
  if (!ts) return '—';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return String(ts).slice(0, 10);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function SettingsModule() {
  const { email, flags, refreshSettings } = useSettingsReadModel();

  return (
    <div style={LAYOUT.subPanel}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.textMuted }}>
          Settings module
        </div>
        <button
          type="button"
          onClick={refreshSettings}
          style={{
            background: 'none',
            border: `1px solid ${C.borderSoft}`,
            borderRadius: 6,
            color: C.textMuted,
            padding: '4px 10px',
            fontSize: 12,
            cursor: 'pointer',
          }}
        >
          Refresh
        </button>
      </div>
      <p style={{ marginTop: 8, marginBottom: 4, lineHeight: 1.6 }}>
        Settings surfaces the repo's real email routing and feature-flag state.
        Every value below is read from the live worker — no toggles, no cached
        config. This surface is read-only: changes to email senders, recipients,
        or cron schedules require a worker deploy; feature flags are flipped
        via `PATCH /api/feature-flags/:name` by keymakers only.
      </p>

      <PanelShell title="Email operations" panel={email}>
        {(data) => (
          <div style={{ display: 'grid', gap: 10 }}>
            <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
              <KV
                label="Resend API key"
                value={data.resend?.configured ? 'Configured' : 'Not configured'}
                accent={data.resend?.configured ? C.green : C.red}
              />
            </div>
            <ChannelCard title="Weekly attendance report"    block={data.weekly_attendance_report} />
            <ChannelCard title="Onboarding reminder digest"  block={data.onboarding_reminder_digest} />
            <ChannelCard title="Contact form"                block={data.contact_form} />
            {data.note && (
              <div style={{ color: C.textMuted, fontSize: 11, lineHeight: 1.5 }}>
                {data.note}
              </div>
            )}
          </div>
        )}
      </PanelShell>

      <PanelShell title="Feature flags" panel={flags}>
        {(rows) => {
          if (!rows.length) {
            return <div style={{ color: C.textMuted, fontSize: 12 }}>No feature flags on record.</div>;
          }
          return (
            <div style={{ display: 'grid', gap: 8 }}>
              {rows.map((f) => (
                <div
                  key={f.id || f.flag_name}
                  style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}`, display: 'grid', gap: 6 }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                    <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{f.flag_name}</div>
                    <span
                      style={{
                        fontSize: 11,
                        padding: '3px 10px',
                        borderRadius: 999,
                        color: f.enabled ? C.green : C.textMuted,
                        background: f.enabled ? 'rgba(78,205,196,0.12)' : 'rgba(255,255,255,0.05)',
                      }}
                    >
                      {f.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <div style={{ color: C.textMuted, fontSize: 12, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <span>Scope: {f.scope || '—'}</span>
                    {f.terminal_id && <span>Terminal: {f.terminal_id}</span>}
                    <span>Updated: {formatFlagUpdatedAt(f.updated_at)}</span>
                  </div>
                  {f.notes && <div style={{ color: C.textMuted, fontSize: 12, lineHeight: 1.5 }}>{f.notes}</div>}
                </div>
              ))}
            </div>
          );
        }}
      </PanelShell>

      <PermissionsPanel />

      <DiagnosticsActivator />
    </div>
  );
}

function DiagnosticsActivator() {
  const [diagEnabled, , toggleDiag] = useDiagMode();
  const [input, setInput] = useState('');
  const [hint, setHint] = useState<string | null>(null);

  function handleSubmit() {
    const normalized = input.trim().toLowerCase();
    setInput('');
    if (normalized === 'xyzzy') {
      toggleDiag();
      setHint(diagEnabled ? 'Diagnostics off for this session.' : 'Diagnostics on for this session. Session tab unlocked.');
    } else if (normalized) {
      setHint('Nothing happens.');
    }
    window.setTimeout(() => setHint(null), 3500);
  }

  return (
    <div style={LAYOUT.subPanel}>
      <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.textMuted, marginBottom: 8 }}>
        Diagnostics
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSubmit();
            }
          }}
          placeholder="Enter a word…"
          autoComplete="off"
          spellCheck={false}
          style={{
            flex: '1 1 220px',
            minWidth: 0,
            padding: '8px 12px',
            background: 'rgba(255,255,255,0.04)',
            border: `1px solid ${C.borderSoft}`,
            borderRadius: 8,
            color: C.text,
            fontSize: 13,
            outline: 'none',
          }}
          aria-label="Diagnostics activation"
        />
        <span
          style={{
            fontSize: 11,
            padding: '3px 10px',
            borderRadius: 999,
            color: diagEnabled ? C.green : C.textMuted,
            background: diagEnabled ? 'rgba(78,205,196,0.12)' : 'rgba(255,255,255,0.04)',
            border: `1px solid ${diagEnabled ? 'rgba(78,205,196,0.30)' : C.borderSoft}`,
            whiteSpace: 'nowrap',
          }}
        >
          {diagEnabled ? 'Diagnostics ON' : 'Diagnostics off'}
        </span>
      </div>
      {hint && (
        <div style={{ marginTop: 8, fontSize: 12, color: C.textMuted }}>{hint}</div>
      )}
      <div style={{ marginTop: 8, fontSize: 11, color: C.textMuted, lineHeight: 1.5 }}>
        Persists across sidebar sections within this session. Any page reload resets to off.
      </div>
    </div>
  );
}
